import React,{ useState } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import Button  from "components/button";
import ButtonWithConfirm from "components/button/buttonwithconfirm";
import PopupButton from "components/button/PopupButton";
import AdvanceCandidate from "./AdvanceCandidate";
import CandidateScoreCard from "./CandidateScoreCard";
import { getNameInitials } from "utils/index";
import {
    Mail,
    Phone,
    MapPin,
    CheckCircle2,
    CircleX,
    Sparkles,
    GraduationCap,
} from "lucide-react";
import { 
	doAdvanceApplicant, doDeclineApplicant
} from "../../../../redux/recruitment/recruitmentState";
import DownloadButton from "components/button/download";

const CandidateProfile = ({ authUser, candidate, updateApplicantStatus, declineApplicant }) => {

    const [isOpen, setIsOpen] = useState(false);
    const isClient = authUser?.user_level === 4;

    const handleConfirm = () => {
        updateApplicantStatus({id:candidate.jap_id, pipelineId:candidate.jap_pipeline_id});
        setIsOpen(false);  
    };

    const handleNotSuitable = () => {
        declineApplicant({id:candidate.jap_id});
        setIsOpen(false);  
    };
    
    if (!candidate) return null;

    const {
        applicant,
        applicant_name,
        applicant_email,
        applicant_phone,
        applicant_location,
        job_post_title,
        role,
        created_at,
        jap_remarks,
    } = candidate;

    return (
        <Card className="flex flex-col gap-6 rounded-2xl border-2 border-emerald-500 shadow-xl bg-white lg:sticky lg:top-6 overflow-visible">
            <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 [.border-b]:pb-6 bg-gradient-to-br from-emerald-50 to-cyan-50 border-b border-slate-200 rounded-t-2xl p-4 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3">
                    <span className="relative flex w-14 h-14 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-md bg-gradient-to-br from-emerald-500 to-cyan-500">
                        <span className="flex size-full items-center justify-center font-semibold text-[16px] sm:text-[18px] text-white">
                            {getNameInitials(applicant_name)}
                        </span>
                    </span>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-[16px] sm:text-[18px] truncate">{applicant_name}</h4>
                        <p className="text-slate-700 text-[12px] sm:text-[13px] truncate">{job_post_title}</p>
                    </div>
                </div>
            </div>

            <div className="pb-6 p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-5 overflow-x-hidden">
                {!isClient && 
                    <>
                    <div>
                        <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                            <Mail className="w-4 h-4 text-emerald-600" /> Contact Information
                        </h4>
                        <div className="space-y-2 text-[12px] text-slate-700">
                            <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <a href={`mailto:${applicant_email}`} className="text-emerald-600 truncate hover:underline">
                                    {applicant_email}
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                <span className="text-slate-900">{applicant_phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="text-slate-900">{applicant_location}</span>
                            </div>
                        </div>
                    </div>

                    <hr className="border-t border-gray-200" />
                    </>
                }

                <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                        <GraduationCap className="w-4 h-4 text-emerald-600" /> Background
                    </h4>
                    <div className="text-[12px] space-y-1.5">
                        <div>
                            <p className="text-slate-700 text-[11px]">Experience</p>
                            <p className="font-medium">{applicant?.apl_xp_years} years</p>
                        </div>
                        <div>
                            <p className="text-slate-700 text-[11px]">Education</p>
                            <p className="font-medium">{applicant?.education}</p>
                        </div>
                        {!isClient &&
                        <div>
                            <p className="text-slate-700 text-[11px]">Applied Date</p>
                            <p className="font-medium">{created_at}</p>
                        </div>
                        }
                    </div>
                </div>

                <hr className="border-t border-gray-200" />

                <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-[13px] mb-2 flex items-center gap-2 text-gray-800">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        <span>Skills</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {applicant?.skills?.map((skill) => (
                            <span
                                key={skill}
                                className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-medium whitespace-nowrap hover:bg-emerald-200 transition"
                            >
                                {skill}
                            </span>
                        ))}
                    </div>
                </div>

                {isClient && 
                    <CandidateScoreCard scoreData={candidate?.applicant_score} isClientUser={true}/>
                }
                
                <hr className="border-t border-gray-200" />

                <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-[13px] mb-2">Notes</h4>
                    <p className="text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-3 text-gray-700 leading-relaxed">
                        {jap_remarks}
                    </p>
                </div>

                <div className="space-y-2">
                    {isClient ?
                        <PopupButton 
                            className="access-button btn-next-stage w-full"
                            label="View Resume"
                            useCustomClass={true}>
                            
                        </PopupButton>
                        :
                        <DownloadButton 
                            className="w-full py-2 text-[13px]"
                            type="recruitment"
                            downloadPath={`download/applicant/${applicant.apl_id}/resume`} 
                            title="Download Resume"/>
                    }

                    {(!isClient || (isClient && candidate.jap_pipeline_id <= 42))&& 
                    <div className="flex w-full gap-2">
                        <ButtonWithConfirm
                            className="access-button btn-decline flex-grow"
                            confirmLabel="Decline"
                            confirmationTitle="Not Suitable"
                            confirmationMessage={<>Are you sure you want to decline <span className="font-medium">{applicant_name}</span>?</>}
                            confirmHandler={handleNotSuitable}
                        >
                            <CircleX className="w-3 h-3" />
                            <span>Not Suitable</span>
                        </ButtonWithConfirm>

                        <ButtonWithConfirm
                            className="access-button btn-next-stage flex-grow"
                            confirmLabel="Advance"
                            confirmationTitle="Advance to Next Stage"
                            confirmationMessage={<>Are you sure you want to advance <span className="font-medium">{applicant_name}</span> to the next stage in the pipeline?</>}
                            confirmHandler={handleConfirm}
                        >
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Advance to Next Stage</span>
                        </ButtonWithConfirm>
                    </div>
                    }
                    {!isClient &&
                    <Button className="w-full h-9 flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2 text-[13px] text-gray-800 font-medium hover:bg-gray-50 transition">
                        <Mail className="w-3 h-3" /> 
                        <span>Contact Candidate</span>
                    </Button>
                    }
                </div>
            </div>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({
    updateApplicantStatus: (data) => dispatch(doAdvanceApplicant(data)),
    declineApplicant: (data) => dispatch(doDeclineApplicant(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CandidateProfile);
